import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Button, Typography, Avatar, Dropdown, Drawer, Image } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  CloudServerOutlined,
  BarChartOutlined,
  SolutionOutlined,
  HddOutlined,
  DollarCircleOutlined,
  ScissorOutlined,
  DesktopOutlined,
  SendOutlined,
  ReadOutlined,
  ContainerOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import {useSettings} from "../../contexts/SettingsContext";
import { authAPI, applicationsAPI } from "../../utils/api";
import { userSessionService, LoggedUserDetails, UserPermissions } from "../../utils/userSession";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface MainLayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, onLogout }) => {

  // Use settings context
  const { settings, loading: settingsLoading } = useSettings();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState<boolean>(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [userSessionLoaded, setUserSessionLoaded] = useState<boolean>(false);
  const [noOfAvailablePins, setNoOfAvailablePins] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Update openKeys when location changes
  useEffect(() => {
    const newOpenKeys = getOpenKeys();
    setOpenKeys(newOpenKeys);

  }, [location.pathname, location.search]);

  // Fetch user session data on component mount
  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        const response = await authAPI.getSession();
        if (response.data) {
          // Save user details to localStorage
          userSessionService.saveUserDetails(response.data as LoggedUserDetails);
          setUserSessionLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching session data:', error);
        // Optionally handle error (e.g., redirect to login)
        setUserSessionLoaded(true); // Set to true even on error to prevent infinite loading
      }
    };

    fetchSessionData();
  }, []);

  // Load rate plan for reseller and calculate available pins
  const loadRatePlanForReseller = async (ratePlanId: number, currentBalance: number) => {
    try {
      const response = await applicationsAPI.getRatePlansDetails(ratePlanId.toString());
      let calculatedPins = 0;

      // Loop through rates to find 30-day plans and calculate available pins
      if (response.data && response.data.rates) {
        for (const rate of response.data.rates) {
          if (rate.no_of_days === 30) {
            const price = parseFloat(rate.price);
            if (!isNaN(price) && price > 0) {
              calculatedPins = Math.floor(currentBalance / price);
              break;
            }
          }
        }
      }

      setNoOfAvailablePins(calculatedPins);
    } catch (error) {
      console.error('Error loading rate plan for reseller:', error);
      setNoOfAvailablePins(0);
    }
  };

  // Load rate plan data when user session is loaded
  useEffect(() => {
    if (userSessionLoaded) {
      const userDetails = userSessionService.getUserDetails();
      if (userDetails && userDetails.reseller) {
        const ratePlanId = userDetails.reseller.rate_plan_id;
        const balance = parseFloat(userDetails.reseller.balance || '0');
        if (ratePlanId && !isNaN(balance)) {
          loadRatePlanForReseller(ratePlanId, balance);
        }
      }
    }
  }, [userSessionLoaded]);

  // Get current selected key based on route
  const getSelectedKey = () => {
    const pathname = location.pathname;
    const search = location.search;
    const fullPath = pathname + search;

    // Handle exact matches first
    if (fullPath === '/resellers?all=1') return ['/resellers?all=1'];
    if (fullPath === '/servers?groups=1') return ['/servers?groups=1'];
    if (fullPath.includes('/reports?type=')) return [fullPath];

    // Handle path-based matches
    if (pathname.startsWith('/subscribers/')) return [pathname];
    if (pathname.startsWith('/applications/')) return [pathname];
    if (pathname.startsWith('/push_management/')) return [pathname];
    if (pathname.startsWith('/settings/')) return [pathname];

    // Handle base paths
    if (pathname === '/dashboard') return ['/dashboard'];
    if (pathname === '/resellers') return ['/resellers/my'];
    if (pathname === '/servers') return ['/servers/list'];
    if (pathname === '/subscribers') return ['/subscribers/single'];
    if (pathname === '/tariff') return ['/tariff'];
    if (pathname === '/discount_code') return ['/discount_code'];
    if (pathname === '/feedback') return ['/feedback'];
    if (pathname === '/settings') return ['/settings'];

    // Handle reseller-specific routes
    if (pathname === '/users') return ['/users'];
    if (pathname === '/analytics') return ['/analytics'];

    // Default to dashboard
    return ['/dashboard'];
  };

  // Get current open keys based on route (which parent menus should be expanded)
  const getOpenKeys = () => {
    const pathname = location.pathname;
    const search = location.search;
    const fullPath = pathname + search;

    // Handle routes that should open specific parent menus
    if (fullPath === '/resellers?all=1' || pathname === '/resellers') return ['/resellers'];
    if (fullPath === '/servers?groups=1' || pathname === '/servers') return ['/servers'];
    if (fullPath.includes('/reports?type=')) return ['/reports'];

    // Handle path-based matches for submenus
    if (pathname.startsWith('/subscribers/')) return ['/subscribers'];
    if (pathname.startsWith('/applications/')) return ['/applications'];
    if (pathname.startsWith('/push_management/')) return ['/push_management'];
    if (pathname.startsWith('/settings/')) return ['/settings'];

    // Reseller routes are all top-level, no parent menus to open
    // No parent menu needs to be open for top-level items
    return [];
  };


  // Get menu items based on environment configuration and user permissions
  const getMenuItems = (): MenuProps['items'] => {
    const routeConfigType = process.env.REACT_APP_ROUTE_CONFIG || 'default';
    const userDetails = userSessionService.getUserDetails();
    const userPermissions = userDetails?.permission || {};

    // Build base menu items based on environment
    let baseMenuItems: MenuProps['items'] = [];

    if (routeConfigType === 'reseller') {
      baseMenuItems = [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: 'Dashboard',
        },
        {
          key: '/resellers',
          icon: <TeamOutlined />,
          label: 'Resellers',
        },
        {
          key: '/subscribers',
          icon: <SolutionOutlined />,
          label: 'Subscribers',
          children: [
            {
              key: '/subscribers/single',
              label: 'Single User',
            },
            {
              key: '/subscribers/batch',
              label: 'Batch',
            },
            {
              key: '/subscribers/connected',
              label: 'Connected',
            },
          ],
        },
        {
          key: '/tariff',
          icon: <DollarCircleOutlined />,
          label: 'Tariff',
          children: [
            {
              key: '/tariff',
              label: 'List',
            },
            {
              key: '/tariff?base_rate=1',
              label: 'Base Tariff',
            },
          ],
        },
        {
          key: '/reports',
          icon: <ReadOutlined />,
          label: 'Reports',
          children: [
            {
              key: '/reports?type=pinsubscriptionreport',
              label: 'Subscription',
            },
            {
              key: '/reports?type=hourlysubscriptionreport',
              label: 'Hourly Subscription',
            },
            {
              key: '/reports?type=onlinetransactionreport',
              label: 'Online Transaction',
            },
          ],
        },
      ];
    } else {
      // Default menu items
      baseMenuItems = [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: 'Dashboard',
        },
        {
          key: '/resellers',
          icon: <TeamOutlined />,
          label: 'Resellers',
          children: [
            {
              key: '/resellers/my',
              label: 'My Resellers',
            },
            {
              key: '/resellers?all=1',
              label: 'All Resellers',
            },
          ],
        },
        {
          key: '/subscribers',
          icon: <SolutionOutlined />,
          label: 'Subscribers',
          children: [
            {
              key: '/subscribers/single',
              label: 'Single User',
            },
            {
              key: '/subscribers/batch',
              label: 'Batch',
            },
            {
              key: '/subscribers/connected',
              label: 'Connected',
            },
          ],
        },
        {
          key: '/servers',
          icon: <HddOutlined />,
          label: 'Servers',
          children: [
            {
              key: '/servers/list',
              label: 'Servers',
            },
            {
              key: '/servers?groups=1',
              label: 'Groups',
            },
          ],
        },
        {
          key: '/tariff',
          icon: <DollarCircleOutlined />,
          label: 'Tariff',
        },
        {
          key: '/discount_code',
          icon: <ScissorOutlined />,
          label: 'Discount Code',
        },
        {
          key: '/reports',
          icon: <ReadOutlined />,
          label: 'Reports',
          children: [
            {
              key: '/reports?type=pinsubscriptionreport',
              label: 'Subscription',
            },
            {
              key: '/reports?type=hourlysubscriptionreport',
              label: 'Hourly Subscription',
            },
            {
              key: '/reports?type=onlinetransactionreport',
              label: 'Online Transaction',
            },
          ],
        },
        {
          key: '/feedback',
          icon: <ContainerOutlined />,
          label: 'Feedback',
        },
        {
          key: '/settings',
          icon: <SettingOutlined />,
          label: 'Settings',
          children: [
            {
              key: '/settings/radius_server',
              label: 'Radius Server',
            },
            {
              key: '/settings/panel',
              label: 'Panel',
            },
            {
              key: '/settings/geographic',
              label: 'Region',
            },
          ],
        },
      ];
    }

    // Add permission-based menu items
    const permissionBasedItems: MenuProps['items'] = [];

    // Add Applications menu if user has banner permission
    if (routeConfigType === 'default' || (routeConfigType === 'reseller' && userPermissions.allow_banner_for_application)) {
      permissionBasedItems.push({
        key: '/applications',
        icon: <DesktopOutlined />,
        label: 'Applications',
        children: [
          {
            key: '/applications/list',
            label: 'App List',
          },
          {
            key: '/applications/banners',
            label: 'Banner List',
          },
        ],
      });
    }

    // Add Push Management menu if user has push notification permission
    if (routeConfigType === 'default' || (routeConfigType === 'reseller' && userPermissions.allow_push_notification)) {
      permissionBasedItems.push({
        key: '/push_management',
        icon: <SendOutlined />,
        label: 'Push Management',
        children: [
          {
            key: '/push_management/templates',
            label: 'Push Template',
          },
          {
            key: '/push_management/push_requests',
            label: 'Send Push',
          },
        ],
      });
    }

    // Combine base menu items with permission-based items
    // Insert permission-based items before the last few items (reports, feedback, settings)
    const finalMenuItems = [...baseMenuItems];

    if (permissionBasedItems.length > 0) {
      // Find the index to insert permission-based items
      // For default environment, insert before reports (which should be near the end)
      // For reseller environment, insert before reports as well
      const reportsIndex = finalMenuItems.findIndex(item => item?.key === '/reports');
      const insertIndex = reportsIndex !== -1 ? reportsIndex : finalMenuItems.length;

      finalMenuItems.splice(insertIndex, 0, ...permissionBasedItems);
    }

    return finalMenuItems;
  };

  const menuItems = useMemo(() => getMenuItems(), [userSessionLoaded]);

  const userMenuItems: MenuProps['items'] = [
    /*{
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      type: 'divider',
    },*/
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: onLogout,
    },
  ];

  const handleMenuToggle = () => {
    if (isMobile) {
      setMobileMenuVisible(!mobileMenuVisible);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuClick = (e: any) => {
    const key = e.key as string;

    // Handle special cases with query parameters
    if (key === '/resellers?all=1') {
      navigate('/resellers?all=1');
    } else if (key === '/servers?groups=1') {
      navigate('/servers?groups=1');
    } else if (key.includes('/reports?type=')) {
      navigate(key);
    } else if (key === '/resellers/my') {
      // Handle "My Resellers" menu item
      navigate('/resellers');
    } else if (key === '/servers/list') {
      // Handle "Servers" menu item
      navigate('/servers');
    } else if (key === '/subscribers/single') {
      // Handle "Single User" menu item
      navigate('/subscribers/single');
    } else if (key === '/subscribers/batch') {
      // Handle "Batch" menu item
      navigate('/subscribers/batch');
    } else if (key === '/subscribers/connected') {
      // Handle "Connected" menu item
      navigate('/subscribers/connected');
    } else if (key === '/users') {
      // Handle reseller "Users" menu item
      navigate('/users');
    } else if (key === '/analytics') {
      // Handle reseller "Analytics" menu item
      navigate('/analytics');
    } else {
      // Handle regular routes
      navigate(key);
    }

    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  const renderSideMenu = () => (
    <>
      <div style={{
        margin: 16,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 'bold'
      }}>
        <Image
            preview={false}
            src={collapsed && !isMobile ? `${process.env.REACT_APP_API_URL}/file/public/view?file=${settings?.small_logo}` : `${process.env.REACT_APP_API_URL}/file/public/view?file=${settings?.logo_images}`}
        />
      </div>
      <Menu
        mode="inline"
        selectedKeys={getSelectedKey()}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={menuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  const userDetails = userSessionService.getUserDetails();

  // Function to render reseller information
  const renderResellerInfo = () => {
    const routeConfigType = process.env.REACT_APP_ROUTE_CONFIG || 'default';

    if (routeConfigType !== 'reseller' || !userDetails) {
      return null;
    }

    const balance = userDetails.resellerInfo?.balance || userDetails.reseller?.balance || '0.0';
    const limitPins = userDetails.resellerInfo?.limit_pins || 0;
    const userLevel = userDetails.user_level || 0;

    // Available subscriptions calculated from rate plan data
    const availableSubscriptions = noOfAvailablePins;

    const handleRefresh = async () => {
      try {
        // Refresh session data
        const response = await authAPI.getSession();
        if (response.data) {
          userSessionService.saveUserDetails(response.data as LoggedUserDetails);

          // Reload rate plan data
          if (response.data.reseller) {
            const ratePlanId = response.data.reseller.rate_plan_id;
            const balance = parseFloat(response.data.reseller.balance || '0');
            if (ratePlanId && !isNaN(balance)) {
              await loadRatePlanForReseller(ratePlanId, balance);
            }
          }
        }
      } catch (error) {
        console.error('Error refreshing data:', error);
        // Fallback to page reload if API calls fail
        window.location.reload();
      }
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginRight: 16 }}>
        <Button
          type="primary"
          shape="round"
          size="small"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
        <Text>
          Balance: <strong>{balance} USD</strong>
        </Text>
        <Text>
          Avail. Subscription: <strong>{availableSubscriptions}</strong>
        </Text>
        {userLevel === 4 && (
          <Text>
            VPN Subscription: 
            <strong>
              {limitPins < 0 ? ' Unlimited' : limitPins > 0 ? ` ${limitPins}` : ` ${limitPins} ${userLevel}`}
            </strong>
          </Text>
        )}
      </div>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider trigger={null} collapsible collapsed={collapsed}>
          {renderSideMenu()}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          title={settings?.com_name || "Loading..."}
          placement="left"
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          bodyStyle={{ padding: 0, backgroundColor: '#001529' }}
          headerStyle={{ backgroundColor: '#001529', color: 'white' }}
          width={250}
        >
          <Menu
            mode="inline"
            selectedKeys={getSelectedKey()}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ border: 'none' }}
          />
        </Drawer>
      )}

      <Layout>
        <Header style={{ 
          padding: '0 16px', 
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={handleMenuToggle}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {!isMobile && renderResellerInfo()}
            <Text style={{ display: isMobile ? 'none' : 'block' }}>Hello, {userDetails?.login || ""}</Text>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar 
                style={{ backgroundColor: '#1890ff', cursor: 'pointer' }} 
                icon={<UserOutlined />} 
              />
            </Dropdown>
          </div>
        </Header>

        <Content style={{ margin: '24px 16px', background: '#fff' }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
